import React, { useState, useEffect } from 'react';
import { Edit3, Save, RefreshCw, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/TestAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Top expense categories for editing (expanded for restaurants)
const TOP_EXPENSE_CATEGORIES = [
  'COGS - Variable',
  'COGS - Fixed',
  'Food and Beverage Inventory',
  'Payroll',
  'Officers Payroll',
  'Rent Expense',
  'Utilities',
  'Insurance',
  'Advertising Expense',
  'Supplies',
  'Maintenance',
  'Auto Expense',
  'Professional Services',
  'Contract Labor',
  'Fuel Expense',
  'Travel & Entertainment',
  'Depreciation',
  'Interest Expense'
];

interface EditableFinancialGridProps {
  reportId: string;
  financialData: any;
  onRecalculated?: (newResult: any) => void;
}

export default function EditableFinancialGrid({ 
  reportId, 
  financialData, 
  onRecalculated 
}: EditableFinancialGridProps) {
  // DYNAMIC: Extract years from financial data
  const years = React.useMemo(() => {
    if (!financialData) return [];
    
    const extractedYears = Object.keys(financialData)
      .filter(key => key.startsWith('data_'))
      .map(key => key.replace('data_', ''))
      .filter(year => !isNaN(parseInt(year)))
      .sort((a, b) => parseInt(a) - parseInt(b));
    
    console.log('✅ EditableFinancialGrid: Dynamic years extracted:', extractedYears);
    return extractedYears;
  }, [financialData]);
  
  // DEBUG: Log what financial data we received
  console.log('📊 EditableFinancialGrid received financialData:', financialData);
  years.forEach(year => {
    console.log(`📊 data_${year}:`, financialData?.[`data_${year}`]?.length || 0, 'months');
  });
  
  const [editedData, setEditedData] = useState(financialData);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<any>(null);
  const [editedCells, setEditedCells] = useState<Set<string>>(new Set());
  const [gridMessage, setGridMessage] = useState<string | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);
  const { getToken } = useAuth();

  // Auto-save when data changes
  useEffect(() => {
    // Debounced auto-save logic can go here
  }, [editedData]);

  const handleCellEdit = (year: string, monthIndex: number, field: string, value: string) => {
    const cellKey = `${year}-${monthIndex}-${field}`;
    setEditedCells(prev => new Set(prev).add(cellKey));
    
    setEditedData((prev: any) => {
      const newData = { ...prev };
      const yearKey = `data_${year}`;
      const monthData = [...(newData[yearKey] || [])];
      
      if (!monthData[monthIndex]) {
        monthData[monthIndex] = { year, month: monthIndex + 1, revenue: 0, expenses: {}, payroll: {} };
      }
      
      if (field === 'revenue') {
        monthData[monthIndex].revenue = parseFloat(value) || 0;
      } else if (field === 'total_payroll') {
        monthData[monthIndex].payroll = { 
          ...monthData[monthIndex].payroll, 
          total: parseFloat(value) || 0 
        };
      } else if (field === 'officer_compensation') {
        monthData[monthIndex].payroll = { 
          ...monthData[monthIndex].payroll, 
          officer_compensation: parseFloat(value) || 0 
        };
      } else {
        // Expense category
        monthData[monthIndex].expenses = {
          ...monthData[monthIndex].expenses,
          [field]: parseFloat(value) || 0
        };
      }
      
      newData[yearKey] = monthData;
      return newData;
    });
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    setRecalcResult(null);
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/reports/${reportId}/recalculate`,
        { financial_data: editedData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRecalcResult(response.data);
      setEditedCells(new Set()); // Clear edited markers
      
      if (onRecalculated) {
        // ✅ FIX: Include edited financial data in callback for chart refresh
        onRecalculated({
          ...response.data,
          financial_data: editedData
        });
      }
    } catch (error: any) {
      console.error('Recalculation failed:', error);
      setGridError(`Recalculation failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsRecalculating(false);
    }
  };

  const renderMonthRow = (year: string, monthIndex: number, monthData: any) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const cellKey = (field: string) => `${year}-${monthIndex}-${field}`;
    const isEdited = (field: string) => editedCells.has(cellKey(field));
    
    return (
      <tr key={`${year}-${monthIndex}`} className="border-b hover:bg-gray-50">
        <td className="px-3 py-2 text-sm font-medium text-gray-700 sticky left-0 bg-white">
          {year} {monthNames[monthIndex]}
        </td>
        
        {/* Revenue */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={monthData?.revenue || 0}
            onChange={(e) => handleCellEdit(year, monthIndex, 'revenue', e.target.value)}
            className={`w-full px-2 py-1 text-sm border rounded ${
              isEdited('revenue') ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
            }`}
          />
        </td>
        
        {/* Total Payroll */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={monthData?.payroll?.total || 0}
            onChange={(e) => handleCellEdit(year, monthIndex, 'total_payroll', e.target.value)}
            className={`w-full px-2 py-1 text-sm border rounded ${
              isEdited('total_payroll') ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
            }`}
          />
        </td>
        
        {/* Officer Compensation */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={monthData?.payroll?.officer_compensation || 0}
            onChange={(e) => handleCellEdit(year, monthIndex, 'officer_compensation', e.target.value)}
            className={`w-full px-2 py-1 text-sm border rounded ${
              isEdited('officer_compensation') ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
            }`}
          />
        </td>
        
        {/* ALL Expense Categories from actual data */}
        {(() => {
          // Get all unique expense categories from all years
          const allCategories = new Set<string>();
          years.forEach(y => {
            const yearKey = `data_${y}`;
            const yearData = editedData[yearKey] || [];
            yearData.forEach((md: any) => {
              if (md?.expenses) {
                Object.keys(md.expenses).forEach(cat => allCategories.add(cat));
              }
            });
          });
          
          // Sort categories in P&L order
          const categoryOrder = [
            'COGS - Variable',
            'COGS - Fixed',
            'Food and Beverage Inventory',
            'Payroll',
            'Officers Payroll',
            'Rent Expense',
            'Utilities',
            'Insurance',
            'Advertising Expense',
            'Supplies',
            'Other'
          ];
          
          const sortedCategories = Array.from(allCategories).sort((a, b) => {
            const aIndex = categoryOrder.indexOf(a);
            const bIndex = categoryOrder.indexOf(b);
            if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
            if (aIndex >= 0) return -1;
            if (bIndex >= 0) return 1;
            return a.localeCompare(b);
          });
          
          return sortedCategories.map((expCategory) => (
            <td key={expCategory} className="px-3 py-2">
              <input
                type="number"
                value={monthData?.expenses?.[expCategory] || 0}
                onChange={(e) => handleCellEdit(year, monthIndex, expCategory, e.target.value)}
                className={`w-full px-2 py-1 text-sm border rounded ${
                  isEdited(expCategory) ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'
                }`}
              />
            </td>
          ));
        })()}
      </tr>
    );
  };

  // Generate P&L CSV from edited data
  const generatePLFromData = (data: any) => {
    // Use the same dynamic years
    const csvYears = years;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Collect all expense categories
    const allCategories = new Set<string>();
    csvYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = data[yearKey] || [];
      yearData.forEach((monthData: any) => {
        if (monthData?.expenses) {
          Object.keys(monthData.expenses).forEach(cat => allCategories.add(cat));
        }
      });
    });
    
    const categories = Array.from(allCategories).sort();
    
    // Build CSV
    let csv = 'Year,Month,Revenue,Total Payroll,Officer Comp';
    categories.forEach(cat => {
      csv += `,${cat}`;
    });
    csv += '\n';
    
    // Add data rows
    csvYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = data[yearKey] || [];
      
      yearData.forEach((monthData: any, idx: number) => {
        csv += `${year},${months[idx]},`;
        csv += `${monthData?.revenue || 0},`;
        csv += `${monthData?.payroll?.total || 0},`;
        csv += `${monthData?.payroll?.officer_compensation || 0}`;
        
        categories.forEach(cat => {
          csv += `,${monthData?.expenses?.[cat] || 0}`;
        });
        
        csv += '\n';
      });
    });
    
    return csv;
  };

  // Helper to set annual total for an expense category (distributes evenly across 12 months)
  const setAnnualTotal = (year: string, category: string, annualTotal: number) => {
    const monthlyAmount = annualTotal / 12;
    
    setEditedData((prev: any) => {
      const newData = { ...prev };
      const yearKey = `data_${year}`;
      const monthData = [...(newData[yearKey] || [])];
      
      for (let i = 0; i < 12; i++) {
        if (!monthData[i]) {
          monthData[i] = { year, month: i + 1, revenue: 0, expenses: {}, payroll: {} };
        }
        monthData[i].expenses = {
          ...monthData[i].expenses,
          [category]: monthlyAmount
        };
        // Mark as edited
        const cellKey = `${year}-${i}-${category}`;
        setEditedCells(prev => new Set(prev).add(cellKey));
      }
      
      newData[yearKey] = monthData;
      return newData;
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Quick Annual Entry Tool */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-3">🚀 Quick Annual Entry</h3>
        <p className="text-sm text-blue-700 mb-3">Set annual totals for expense categories (distributes evenly across 12 months)</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select id="quick-category" className="w-full border border-gray-300 rounded px-3 py-2">
              {TOP_EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select id="quick-year" className="w-full border border-gray-300 rounded px-3 py-2">
              <option value="2019">2019</option>
              <option value="2020">2020</option>
              <option value="2021">2021</option>
              <option value="2022">2022</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Annual Total</label>
            <input 
              type="number" 
              id="quick-amount"
              placeholder="192600"
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                const category = (document.getElementById('quick-category') as HTMLSelectElement)?.value;
                const year = (document.getElementById('quick-year') as HTMLSelectElement)?.value;
                const amount = parseFloat((document.getElementById('quick-amount') as HTMLInputElement)?.value || '0');
                if (category && year && amount > 0) {
                  setAnnualTotal(year, category, amount);
                  setGridMessage(`Set ${category} for ${year}: $${amount.toLocaleString()} (${(amount/12).toLocaleString()}/month)`);
                }
              }}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700"
            >
              Apply Annual Total
            </button>
          </div>
        </div>
      </div>

      {/* Quick Monthly Entry Tool */}
      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="font-semibold text-green-900 mb-3">📅 Quick Monthly Entry</h3>
        <p className="text-sm text-green-700 mb-3">Set individual amounts for each month</p>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select id="monthly-category" className="w-full border border-gray-300 rounded px-3 py-2">
              {TOP_EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select id="monthly-year" className="w-full border border-gray-300 rounded px-3 py-2">
              <option value="2019">2019</option>
              <option value="2020">2020</option>
              <option value="2021">2021</option>
              <option value="2022">2022</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
            <div key={month}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{month}</label>
              <input
                type="number"
                id={`month-${idx}`}
                placeholder="0"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            const category = (document.getElementById('monthly-category') as HTMLSelectElement)?.value;
            const year = (document.getElementById('monthly-year') as HTMLSelectElement)?.value;
            
            if (!category || !year) return;
            
            const monthlyAmounts: number[] = [];
            for (let i = 0; i < 12; i++) {
              const input = document.getElementById(`month-${i}`) as HTMLInputElement;
              monthlyAmounts.push(parseFloat(input?.value || '0'));
            }
            
            // Apply monthly amounts
            setEditedData((prev: any) => {
              const newData = { ...prev };
              const yearKey = `data_${year}`;
              const monthData = [...(newData[yearKey] || [])];
              
              for (let i = 0; i < 12; i++) {
                if (!monthData[i]) {
                  monthData[i] = { year, month: i + 1, revenue: 0, expenses: {}, payroll: {} };
                }
                monthData[i].expenses = {
                  ...monthData[i].expenses,
                  [category]: monthlyAmounts[i]
                };
                // Mark as edited
                const cellKey = `${year}-${i}-${category}`;
                setEditedCells(prev => new Set(prev).add(cellKey));
              }
              
              newData[yearKey] = monthData;
              return newData;
            });
            
            const total = monthlyAmounts.reduce((sum, amt) => sum + amt, 0);
            setGridMessage(`Set ${category} for ${year}: $${total.toLocaleString()} total (monthly amounts applied)`);
            
            // Clear inputs
            for (let i = 0; i < 12; i++) {
              const input = document.getElementById(`month-${i}`) as HTMLInputElement;
              if (input) input.value = '';
            }
          }}
          className="w-full bg-green-600 text-white px-4 py-2 rounded font-semibold hover:bg-green-700"
        >
          Apply Monthly Amounts
        </button>
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Edit3 className="w-6 h-6 text-blue-600" />
            48-Month Financial Data Editor
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Edit any cell • Changes saved automatically • Click Recalculate to update claim
          </p>
        </div>
        
        {/* Export Button */}
        <button
          onClick={() => {
            // Generate P&L from edited data
            const csv = generatePLFromData(editedData);
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PL_Statement_Edited_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export P&L to CSV
        </button>
      </div>
      
      {/* Recalculate Button */}
      <div className="mb-6">
        <button
          onClick={handleRecalculate}
          disabled={isRecalculating || editedCells.size === 0}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition ${
            editedCells.size > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isRecalculating ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Recalculating...
            </>
          ) : (
            <>
              <RefreshCw className="w-5 h-5" />
              Recalculate Claim
              {editedCells.size > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-full">
                  {editedCells.size} edits
                </span>
              )}
            </>
          )}
        </button>
      </div>

      {/* Recalculation Result */}
      {recalcResult && (
        <div className={`mb-6 p-4 rounded-lg ${
          recalcResult.delta >= 0 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
        } border-2`}>
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6 text-green-600" />
            <div className="flex-1">
              <p className="font-bold text-lg">
                Claim Recalculated: ${recalcResult.original_claim.toLocaleString()} → ${recalcResult.new_claim.toLocaleString()}
              </p>
              <p className="text-sm text-gray-700">
                Change: {recalcResult.delta >= 0 ? '+' : ''}${recalcResult.delta.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editable Grid */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-10">
                Month
              </th>
              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Revenue</th>
              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Total Payroll</th>
              <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Officer Comp</th>
              {/* Show ALL expense categories from your P&L data */}
              {(() => {
                // Get all unique expense categories from actual data
                const allCategories = new Set<string>();
                years.forEach(year => {
                  const yearKey = `data_${year}`;
                  const yearData = editedData[yearKey] || [];
                  yearData.forEach((monthData: any) => {
                    if (monthData?.expenses) {
                      Object.keys(monthData.expenses).forEach(cat => allCategories.add(cat));
                    }
                  });
                });
                
                // Show categories in P&L order if they exist, otherwise alphabetical
                const categoryOrder = [
                  'COGS - Variable',
                  'COGS - Fixed',
                  'Food and Beverage Inventory',
                  'Payroll',
                  'Officers Payroll',
                  'Rent Expense',
                  'Utilities',
                  'Insurance',
                  'Advertising Expense',
                  'Supplies',
                  'Other'
                ];
                
                const sortedCategories = Array.from(allCategories).sort((a, b) => {
                  const aIndex = categoryOrder.indexOf(a);
                  const bIndex = categoryOrder.indexOf(b);
                  if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
                  if (aIndex >= 0) return -1;
                  if (bIndex >= 0) return 1;
                  return a.localeCompare(b);
                });
                
                return sortedCategories.map((cat) => (
                  <th key={cat} className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                    {cat}
                  </th>
                ));
              })()}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {years.map((year) => {
              const yearKey = `data_${year}`;
              const yearData = editedData[yearKey] || [];
              
              return Array.from({ length: 12 }).map((_, monthIndex) => 
                renderMonthRow(year, monthIndex, yearData[monthIndex])
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-yellow-500 bg-yellow-50 rounded"></div>
          <span>Edited cells</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-gray-500" />
          <span>"Other" = sum of remaining expense categories</span>
        </div>
      </div>
    </div>
  );
}
